import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowRight,
  Bell,
  Copy,
  Gift,
  ImagePlus,
  Link2,
  MapPinned,
  Send,
  Trash2,
  Upload,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { api } from '../lib/api';
import RidesLiveMap from '../components/RidesLiveMap';
import EventGalleryTab from '../components/EventGalleryTab';
import GuestLinksTab from '../components/GuestLinksTab';

type Tab = 'rsvps' | 'links' | 'gifts' | 'budget' | 'rides' | 'gallery';

const TABS: { id: Tab; label: string; icon: typeof Users }[] = [
  { id: 'rsvps', label: 'אורחים', icon: Users },
  { id: 'links', label: 'קישורים לשליחה', icon: Link2 },
  { id: 'gifts', label: 'מתנות', icon: Gift },
  { id: 'budget', label: 'תקציב', icon: Wallet },
  { id: 'rides', label: 'טרמפים', icon: MapPinned },
  { id: 'gallery', label: 'גלריית אירוע', icon: ImagePlus },
];

const STATUS: Record<string, { label: string; cls: string }> = {
  pending: { label: 'ממתין', cls: 'bg-paper-mist text-ink-mute' },
  sent: { label: 'נשלח', cls: 'bg-sky-50 text-sky-700' },
  confirmed: { label: 'אישר', cls: 'bg-sage-50 text-sage-700' },
  declined: { label: 'דחה', cls: 'bg-red-50 text-red-600' },
  maybe: { label: 'אולי', cls: 'bg-brass-50 text-brass-600' },
};

const PUBLIC_WEB =
  (import.meta.env.VITE_PUBLIC_WEB_URL as string | undefined)?.replace(/\/$/, '') ||
  window.location.origin;

type EventInfo = {
  name: string;
  event_date: string;
  invitation_text?: string | null;
  invitation_image_url?: string | null;
  public_token?: string | null;
};

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<Tab>('rsvps');
  const [event, setEvent] = useState<EventInfo | null>(null);

  const reloadEvent = useCallback(() => {
    api
      .get(`/events/${id}`)
      .then((r) => setEvent(r.data))
      .catch(() => undefined);
  }, [id]);

  useEffect(() => {
    reloadEvent();
  }, [reloadEvent]);

  if (tab === 'rides') {
    return <RidesLiveMap eventId={id!} onExit={() => setTab('rsvps')} />;
  }

  return (
    <div className="space-y-7 page-enter">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-ink-faint hover:text-sage-600 transition-colors duration-200 cursor-pointer"
      >
        <ArrowRight size={15} /> חזרה לאירועים
      </Link>

      <div>
        <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-sage-600 mb-2">Event</p>
        <h1 className="font-sans text-4xl font-bold text-ink tracking-tight">{event?.name ?? '…'}</h1>
        {event && (
          <p className="text-ink-mute mt-1.5 text-[15px]">
            {new Date(event.event_date).toLocaleDateString('he-IL', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </p>
        )}
      </div>

      <div className="tab-rail overflow-x-auto max-w-full">
        {TABS.map(({ id: k, label, icon: Icon }) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={tab === k ? 'tab-active' : 'tab'}
          >
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <Icon size={15} strokeWidth={2} />
              {label}
            </span>
          </button>
        ))}
      </div>

      {tab === 'rsvps' && (
        <RsvpsTab eventId={id!} event={event} onEventSaved={reloadEvent} onOpenLinks={() => setTab('links')} />
      )}
      {tab === 'links' && (
        <GuestLinksTab eventId={id!} eventName={event?.name ?? 'החתונה'} />
      )}
      {tab === 'gifts' && <GiftsTab eventId={id!} />}
      {tab === 'budget' && <BudgetTab eventId={id!} />}
      {tab === 'gallery' && <EventGalleryTab eventId={id!} />}
    </div>
  );
}

type Rsvp = {
  id: string;
  guest_name: string;
  guest_phone: string;
  status: string;
  attending_count: number | null;
  invite_token?: string | null;
  guest_collection_link_id?: string | null;
};

function RsvpsTab({
  eventId,
  event,
  onEventSaved,
  onOpenLinks,
}: {
  eventId: string;
  event: EventInfo | null;
  onEventSaved: () => void;
  onOpenLinks: () => void;
}) {
  const [rows, setRows] = useState<Rsvp[]>([]);
  const [linkLabels, setLinkLabels] = useState<Record<string, string>>({});
  const [filterLink, setFilterLink] = useState<string>('all');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [sending, setSending] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [inviteText, setInviteText] = useState('');
  const [inviteImage, setInviteImage] = useState('');
  const [savingText, setSavingText] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [queue, setQueue] = useState<{ queued: number; sent: number; failed: number } | null>(null);

  const load = useCallback(async () => {
    const r = await api.get(`/events/${eventId}/rsvps`);
    setRows(r.data);
  }, [eventId]);

  const loadLinks = useCallback(async () => {
    try {
      const r = await api.get(`/events/${eventId}/guest-collection-links`);
      const map: Record<string, string> = {};
      for (const l of r.data.links ?? []) {
        if (l.id) map[l.id] = l.label || 'קישור ללא שם';
      }
      setLinkLabels(map);
    } catch {
      /* ignore */
    }
  }, [eventId]);

  const loadQueue = useCallback(async () => {
    try {
      const r = await api.get(`/events/${eventId}/outbound/stats`);
      setQueue(r.data);
    } catch {
      /* ignore */
    }
  }, [eventId]);

  useEffect(() => {
    void load();
    void loadQueue();
    void loadLinks();
  }, [load, loadQueue, loadLinks]);

  useEffect(() => {
    setInviteText(event?.invitation_text ?? '');
    setInviteImage(event?.invitation_image_url ?? '');
  }, [event?.invitation_text, event?.invitation_image_url]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) return;
    await api.post(`/events/${eventId}/rsvps`, {
      guest_name: name.trim(),
      guest_phone: phone.trim(),
    });
    setName('');
    setPhone('');
    void load();
  }

  async function sendInvites() {
    setSending(true);
    try {
      const r = await api.post(`/events/${eventId}/invitation/send`, {});
      toast.success(`${r.data.queued} הזמנות נכנסו לתור`);
      void load();
      void loadQueue();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'שגיאה בשליחה');
    } finally {
      setSending(false);
    }
  }

  async function sendReminders() {
    setReminding(true);
    try {
      const r = await api.post(`/events/${eventId}/invitation/send`, { kind: 'rsvp_reminder' });
      toast.success(`${r.data.queued} תזכורות נכנסו לתור`);
      void loadQueue();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'שגיאה בשליחת תזכורות');
    } finally {
      setReminding(false);
    }
  }

  async function saveInvite() {
    setSavingText(true);
    try {
      await api.patch(`/events/${eventId}`, {
        invitation_text: inviteText.trim() || null,
        invitation_image_url: inviteImage.trim() || null,
      });
      toast.success('ההזמנה נשמרה');
      onEventSaved();
    } catch {
      toast.error('שגיאה בשמירה');
    } finally {
      setSavingText(false);
    }
  }

  async function uploadInviteImage(file: File) {
    if (!file.type.startsWith('image/')) {
      toast.error('נא לבחור קובץ תמונה');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error('הקובץ גדול מדי (עד 15MB)');
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const r = await api.post(`/events/${eventId}/media/upload`, {
        filename: file.name,
        content_type: file.type || 'image/jpeg',
        data: dataUrl,
      });
      const url = r.data.url as string;
      setInviteImage(url);
      toast.success('התמונה הועלתה — תישלח עם ההזמנה בוואטסאפ');
      onEventSaved();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'שגיאה בהעלאת התמונה');
    } finally {
      setUploading(false);
    }
  }

  async function clearInviteImage() {
    try {
      await api.delete(`/events/${eventId}/media`);
      setInviteImage('');
      toast.success('התמונה הוסרה');
      onEventSaved();
    } catch {
      toast.error('שגיאה בהסרה');
    }
  }

  function copyInviteLink() {
    toast('כל אורח מקבל קישור אישי בשליחת WhatsApp — אפשר גם להעתיק מהרשימה למטה');
  }

  function copyGuestLink(r: Rsvp) {
    if (!r.invite_token) {
      toast.error('אין קישור לאורח — שמרו ושלחו הזמנה פעם אחת');
      return;
    }
    const url = `${PUBLIC_WEB}/i/${r.invite_token}`;
    void navigator.clipboard.writeText(url).then(
      () => toast.success('קישור אישי הועתק'),
      () => toast.error('לא הצלחנו להעתיק'),
    );
  }

  const confirmed = rows.filter((r) => r.status === 'confirmed').length;
  const usedLinkIds = [...new Set(rows.map((r) => r.guest_collection_link_id).filter((id): id is string => !!id && !!linkLabels[id]))];
  const hasDirectGuests = rows.some((r) => !r.guest_collection_link_id);
  const visibleRows = rows.filter((r) => {
    if (filterLink === 'all') return true;
    if (filterLink === 'none') return !r.guest_collection_link_id;
    return r.guest_collection_link_id === filterLink;
  });

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="surface p-4 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <h2 className="font-semibold text-ink text-[15px]">הזמנה ואישורי הגעה</h2>
          {queue && (
            <p className="text-[12px] text-ink-faint" dir="ltr">
              תור: queued {queue.queued} · sent {queue.sent} · failed {queue.failed}
            </p>
          )}
        </div>
        <p className="text-[13px] text-ink-mute">
          כל אורח מקבל קישור אישי בוואטסאפ. בעמוד: קודם «מגיעים?» ואם כן — «כמה תהיו?» (בלי להזין שם/טלפון).
        </p>
        <label className="block text-[12px] text-ink-mute">
          טקסט הודעת WhatsApp (אופציונלי — {'{name}'}, {'{link}'}, {'{venue}'}, {'{waze}'}, {'{maps}'}, {'{address}'})
          <textarea
            value={inviteText}
            onChange={(e) => setInviteText(e.target.value)}
            rows={4}
            className="field mt-1.5 w-full"
            placeholder="ריק = הודעה עם כתובת אולם, Waze/Maps וקישור אישי לאישור"
          />
        </label>
        <div className="space-y-2">
          <p className="text-[12px] font-medium text-ink-mute">תמונת הזמנה</p>
          <p className="text-[12px] text-ink-faint leading-relaxed">
            העלו תמונה ישירות (נשמרת ב־Supabase). בשליחת WhatsApp האורח מקבל את התמונה יחד עם
            הטקסט — לא רק קישור.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="btn-secondary cursor-pointer !gap-2">
              {uploading ? (
                <>
                  <span className="h-4 w-4 rounded-full border-2 border-sage-300 border-t-sage-700 animate-spin" />
                  מעלה…
                </>
              ) : (
                <>
                  <Upload size={15} aria-hidden />
                  בחירת תמונה
                </>
              )}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,image/heic,.jpg,.jpeg,.png,.webp,.gif"
                className="sr-only"
                disabled={uploading}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void uploadInviteImage(f);
                }}
              />
            </label>
            {inviteImage.trim() && (
              <button
                type="button"
                onClick={() => void clearInviteImage()}
                className="btn-ghost !px-3 text-red-700"
                disabled={uploading}
              >
                <X size={15} aria-hidden />
                הסרה
              </button>
            )}
          </div>
          {inviteImage.trim() ? (
            <div className="relative inline-block mt-1">
              <img
                src={inviteImage.trim()}
                alt="תצוגה מקדימה של הזמנה"
                className="max-h-48 rounded-2xl border border-line object-cover shadow-lift"
              />
              <span className="absolute top-2 right-2 chip bg-sage-600 text-white">
                <ImagePlus size={12} aria-hidden /> מוכן לוואטסאפ
              </span>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-line bg-paper-mist/50 px-4 py-8 text-center text-[13px] text-ink-faint">
              עדיין אין תמונה — בחרו קובץ מהמכשיר
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={saveInvite} disabled={savingText} className="btn-secondary">
            {savingText ? 'שומר…' : 'שמירת הזמנה'}
          </button>
          <button type="button" onClick={copyInviteLink} className="btn-secondary">
            <Copy size={15} />
            קישור אישי
          </button>
          <button type="button" onClick={sendInvites} disabled={sending} className="btn-primary">
            <Send size={15} />
            {sending ? 'שולח…' : 'שליחת הזמנות בוואטסאפ'}
          </button>
          <button type="button" onClick={sendReminders} disabled={reminding} className="btn-secondary">
            <Bell size={15} />
            {reminding ? 'שולח…' : 'תזכורות'}
          </button>
        </div>
      </div>

      <button
        type="button"
        onClick={onOpenLinks}
        className="surface p-4 w-full text-right flex items-center gap-3 hover:bg-paper/70 transition-colors cursor-pointer"
      >
        <div className="h-10 w-10 rounded-2xl bg-sage-100 text-sage-700 grid place-items-center shrink-0">
          <Link2 size={18} aria-hidden />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-ink text-[15px]">קישורים לשליחה למשפחה</p>
          <p className="text-[13px] text-ink-mute mt-0.5">
            צרו קישור לאמא / אבא / חברים — הם מזינים אורחים, ואתם רואים מי הזין כל אחד
          </p>
        </div>
        <span className="text-sage-700 text-[13px] font-semibold shrink-0">למסך הקישורים</span>
      </button>

      <div className="flex flex-col lg:flex-row gap-3 lg:items-end justify-between">
        <form onSubmit={add} className="flex flex-col sm:flex-row gap-2 flex-1">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="שם אורח"
            className="field sm:flex-1"
            aria-label="שם אורח"
          />
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="טלפון"
            className="field sm:w-40"
            dir="ltr"
            aria-label="טלפון"
          />
          <button type="submit" className="btn-secondary">
            הוספה
          </button>
        </form>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-[12px] text-ink-faint">
        <span>
          סה״כ <strong className="text-ink">{rows.length}</strong>
        </span>
        <span>·</span>
        <span>
          אישרו <strong className="text-sage-600">{confirmed}</strong>
        </span>
        {filterLink !== 'all' && (
          <>
            <span>·</span>
            <span>
              מוצגים <strong className="text-ink">{visibleRows.length}</strong>
            </span>
          </>
        )}
      </div>

      {usedLinkIds.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={`chip cursor-pointer ${filterLink === 'all' ? 'bg-sage-600 text-white' : 'bg-paper-mist text-ink-mute'}`}
            onClick={() => setFilterLink('all')}
          >
            כל המקורות
          </button>
          {usedLinkIds.map((id) => {
            const count = rows.filter((r) => r.guest_collection_link_id === id).length;
            const on = filterLink === id;
            return (
              <button
                key={id}
                type="button"
                className={`chip cursor-pointer ${on ? 'bg-sage-600 text-white' : 'bg-paper-mist text-ink-mute'}`}
                onClick={() => setFilterLink(on ? 'all' : id)}
              >
                {linkLabels[id]} · {count}
              </button>
            );
          })}
          {hasDirectGuests && (
            <button
              type="button"
              className={`chip cursor-pointer ${filterLink === 'none' ? 'bg-sage-600 text-white' : 'bg-paper-mist text-ink-mute'}`}
              onClick={() => setFilterLink(filterLink === 'none' ? 'all' : 'none')}
            >
              הוזנו ישירות
            </button>
          )}
        </div>
      )}

      <div className="surface overflow-hidden divide-y divide-line">
        {visibleRows.length === 0 ? (
          <EmptyState text="אין אורחים עדיין — הוסיפו את הראשון או שלחו קישור למשפחה" />
        ) : (
          visibleRows.map((r) => {
            const st = STATUS[r.status] ?? STATUS.pending;
            const source = r.guest_collection_link_id ? linkLabels[r.guest_collection_link_id] : null;
            return (
              <div
                key={r.id}
                className="px-4 py-3.5 flex items-center justify-between gap-3 hover:bg-paper/60 transition-colors"
              >
                <div className="min-w-0">
                  <div className="font-semibold text-ink truncate">{r.guest_name}</div>
                  <div className="text-[12px] text-ink-faint mt-0.5" dir="ltr">
                    {r.guest_phone}
                  </div>
                  {source ? (
                    <div className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-sage-50 text-sage-800 text-[11px] font-semibold px-2 py-0.5">
                      הוזן דרך: {source}
                    </div>
                  ) : (
                    <div className="mt-1.5 text-[11px] text-ink-faint">הוזן ישירות על ידי הזוג</div>
                  )}
                </div>
                <div className="flex items-center gap-2.5 shrink-0">
                  {r.attending_count != null && (
                    <span className="text-[12px] text-ink-mute">{r.attending_count} מגיעים</span>
                  )}
                  <span className={`chip ${st.cls}`}>{st.label}</span>
                  <button
                    type="button"
                    className="btn-ghost !px-2 !py-1.5"
                    title="העתקת קישור אישי"
                    onClick={() => copyGuestLink(r)}
                  >
                    <Copy size={14} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

type Gift = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string;
  amount: number | null;
  blessing: string | null;
  payment_method: string | null;
  source: string;
  rsvp_guest_name: string | null;
};

const PAY_LABEL: Record<string, string> = {
  bit: 'ביט',
  paybox: 'פייבוקס',
  cash: 'מזומן',
  check: 'צ׳ק',
  other: 'אחר',
};
const SOURCE_LABEL: Record<string, string> = {
  manual: 'ידני',
  whatsapp: 'וואטסאפ',
  import: 'ייבוא',
};

function GiftsTab({ eventId }: { eventId: string }) {
  const [rows, setRows] = useState<Gift[]>([]);
  const [first, setFirst] = useState('');
  const [phone, setPhone] = useState('');
  const [amount, setAmount] = useState('');
  const [pay, setPay] = useState('');
  const [blessing, setBlessing] = useState('');

  const load = useCallback(async () => {
    const r = await api.get(`/events/${eventId}/gift-entries`);
    setRows(r.data);
  }, [eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!phone.trim()) return;
    await api.post(`/events/${eventId}/gift-entries`, {
      first_name: first.trim() || null,
      phone: phone.trim(),
      amount: amount.trim() ? parseFloat(amount) : null,
      payment_method: pay || null,
      blessing: blessing.trim() || null,
    });
    setFirst('');
    setPhone('');
    setAmount('');
    setPay('');
    setBlessing('');
    void load();
  }

  async function remove(id: string) {
    await api.delete(`/events/${eventId}/gift-entries/${id}`);
    void load();
  }

  const total = rows.reduce((s, g) => s + (Number(g.amount) || 0), 0);

  return (
    <div className="space-y-4 animate-fade-in">
      <form onSubmit={add} className="surface p-4 sm:p-5 flex flex-wrap gap-2 items-end">
        <div className="min-w-[120px] flex-1">
          <label className="label">שם</label>
          <input value={first} onChange={(e) => setFirst(e.target.value)} className="field" />
        </div>
        <div className="min-w-[120px] flex-1">
          <label className="label">טלפון</label>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} className="field" dir="ltr" required />
        </div>
        <div className="w-28">
          <label className="label">סכום</label>
          <input value={amount} onChange={(e) => setAmount(e.target.value)} className="field" placeholder="₪" />
        </div>
        <div className="w-36">
          <label className="label">תשלום</label>
          <select value={pay} onChange={(e) => setPay(e.target.value)} className="field">
            <option value="">—</option>
            {Object.entries(PAY_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-[140px] flex-[2]">
          <label className="label">ברכה</label>
          <input value={blessing} onChange={(e) => setBlessing(e.target.value)} className="field" />
        </div>
        <button type="submit" className="btn-secondary">
          הוספה
        </button>
      </form>

      <div className="text-[13px] text-ink-mute">
        סה״כ{' '}
        <span className="font-bold text-ink text-base">₪{total.toLocaleString('he-IL')}</span>
        <span className="text-ink-faint"> · {rows.length} מתנות</span>
      </div>

      <div className="surface overflow-hidden divide-y divide-line">
        {rows.length === 0 ? (
          <EmptyState text="אין מתנות עדיין" />
        ) : (
          rows.map((g) => (
            <div key={g.id} className="px-4 py-3.5 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-ink flex items-center gap-2 flex-wrap">
                  {g.rsvp_guest_name ||
                    [g.first_name, g.last_name].filter(Boolean).join(' ') ||
                    '—'}
                  {g.rsvp_guest_name && (
                    <span className="chip bg-sage-50 text-sage-700">משויך</span>
                  )}
                </div>
                <div className="text-[12px] text-ink-faint mt-0.5" dir="ltr">
                  {g.phone}
                </div>
                {g.blessing && (
                  <div className="text-[13px] text-ink-mute mt-1.5 italic truncate">
                    “{g.blessing}”
                  </div>
                )}
                <div className="flex gap-1.5 mt-2">
                  {g.payment_method && (
                    <span className="chip bg-paper-mist text-ink-mute">
                      {PAY_LABEL[g.payment_method] || g.payment_method}
                    </span>
                  )}
                  <span className="chip bg-paper-mist text-ink-faint">
                    {SOURCE_LABEL[g.source] || g.source}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {g.amount != null && (
                  <span className="font-bold text-sage-700">
                    ₪{Number(g.amount).toLocaleString('he-IL')}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => remove(g.id)}
                  className="p-1.5 rounded-lg text-ink-faint hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                  aria-label="מחק"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

type BudgetItem = {
  id: string;
  name: string;
  category: string | null;
  quantity: number;
  unit_price: number;
  actual_price: number | null;
  status: 'planned' | 'paid' | 'cancelled';
  notes: string | null;
};

const BUDGET_STATUS: Record<string, { label: string; cls: string }> = {
  planned: { label: 'מתוכנן', cls: 'bg-sky-50 text-sky-700' },
  paid: { label: 'שולם', cls: 'bg-sage-50 text-sage-700' },
  cancelled: { label: 'בוטל', cls: 'bg-paper-mist text-ink-faint line-through' },
};
const NEXT_STATUS: Record<string, BudgetItem['status']> = {
  planned: 'paid',
  paid: 'cancelled',
  cancelled: 'planned',
};

function rowTotal(it: BudgetItem) {
  return it.actual_price ?? it.unit_price * it.quantity;
}

function BudgetTab({ eventId }: { eventId: string }) {
  const [rows, setRows] = useState<BudgetItem[]>([]);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [qty, setQty] = useState('1');

  const load = useCallback(async () => {
    const r = await api.get(`/events/${eventId}/budget`);
    setRows(r.data);
  }, [eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    await api.post(`/events/${eventId}/budget`, {
      name: name.trim(),
      category: category.trim() || 'שונות',
      unit_price: unitPrice.trim() ? parseFloat(unitPrice) : 0,
      quantity: parseInt(qty, 10) || 1,
    });
    setName('');
    setCategory('');
    setUnitPrice('');
    setQty('1');
    void load();
  }

  async function cycleStatus(it: BudgetItem) {
    await api.patch(`/events/${eventId}/budget/${it.id}`, { status: NEXT_STATUS[it.status] });
    void load();
  }

  async function remove(id: string) {
    await api.delete(`/events/${eventId}/budget/${id}`);
    void load();
  }

  const active = rows.filter((r) => r.status !== 'cancelled');
  const totalPlanned = active.reduce((s, r) => s + r.unit_price * r.quantity, 0);
  const totalPaid = rows.filter((r) => r.status === 'paid').reduce((s, r) => s + rowTotal(r), 0);
  const totalLeft = totalPlanned - totalPaid;

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="מתוכנן" value={totalPlanned} tone="ink" />
        <Stat label="שולם" value={totalPaid} tone="sage" />
        <Stat label="נותר" value={totalLeft} tone={totalLeft < 0 ? 'danger' : 'brass'} />
      </div>

      <form onSubmit={add} className="surface p-4 sm:p-5 flex flex-wrap gap-2 items-end">
        <div className="min-w-[160px] flex-[2]">
          <label className="label">פריט</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="צלם, די.ג׳יי…"
            className="field"
            required
          />
        </div>
        <div className="w-32">
          <label className="label">קטגוריה</label>
          <input value={category} onChange={(e) => setCategory(e.target.value)} className="field" />
        </div>
        <div className="w-28">
          <label className="label">מחיר</label>
          <input value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} className="field" placeholder="₪" />
        </div>
        <div className="w-20">
          <label className="label">כמות</label>
          <input value={qty} onChange={(e) => setQty(e.target.value)} className="field" />
        </div>
        <button type="submit" className="btn-secondary">
          הוספה
        </button>
      </form>

      <div className="surface overflow-hidden divide-y divide-line">
        {rows.length === 0 ? (
          <EmptyState text="אין פריטי תקציב עדיין" />
        ) : (
          rows.map((it) => {
            const st = BUDGET_STATUS[it.status];
            return (
              <div key={it.id} className="px-4 py-3.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-ink">{it.name}</div>
                  <div className="text-[12px] text-ink-faint mt-0.5">
                    {it.category}
                    {it.quantity > 1 ? ` · ×${it.quantity}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2.5 shrink-0">
                  <span className="font-bold text-ink">₪{rowTotal(it).toLocaleString('he-IL')}</span>
                  <button
                    type="button"
                    onClick={() => cycleStatus(it)}
                    className={`chip cursor-pointer hover:opacity-80 transition-opacity ${st.cls}`}
                    title="לחצו לשינוי סטטוס"
                  >
                    {st.label}
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(it.id)}
                    className="p-1.5 rounded-lg text-ink-faint hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                    aria-label="מחק"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'ink' | 'sage' | 'brass' | 'danger';
}) {
  const toneCls = {
    ink: 'text-ink',
    sage: 'text-sage-600',
    brass: 'text-brass-600',
    danger: 'text-red-600',
  }[tone];
  return (
    <div className="surface-quiet px-3 py-4 text-center">
      <div className="text-[11px] font-medium tracking-wide text-ink-faint uppercase">{label}</div>
      <div className={`mt-1 text-xl font-bold tabular-nums ${toneCls}`}>
        ₪{value.toLocaleString('he-IL')}
      </div>
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return <div className="px-6 py-14 text-center text-ink-faint text-sm">{text}</div>;
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('קריאת הקובץ נכשלה'));
    reader.readAsDataURL(file);
  });
}
